import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Stack,
  FormErrorMessage,
  HStack,
  Text,
  ModalCloseButton,
  Select,
} from "@chakra-ui/react";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay } from "@chakra-ui/react";
import { useFormik } from "formik";
import { lowerCase } from "lodash";
import * as Yup from "yup";

import OrganizationService from "@/services/organizationService";

const CreateMIDModal = ({ isOpen, onClose, refreshData, setIsMidLoading, subOrgs, showToast }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const merchantOptions = subOrgs
    ?.filter((org) => org.orgLevel === "merchant")
    ?.map((org) => ({
      value: org.mnemonic,
      label: org.formattedName,
    }));

  const formik = useFormik({
    initialValues: {
      orgLevel: "Merchant",
      name: "",
      merchantMnemonic: "",
      website: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Merchant name is required"),
      website: Yup.string().nullable(),
      orgLevel: Yup.string().required("Select entity type"),
      merchantMnemonic: Yup.string().when("orgLevel", {
        is: "Sub Merchant",
        then: () => Yup.string().required("Merchant mnemonic is required"),
        otherwise: () => Yup.string().nullable(),
      }),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);

        const isSubMerchant = values.orgLevel === "Sub Merchant";
        const { name, merchantMnemonic = "", website } = values;

        const response = await OrganizationService[isSubMerchant ? "createSubMerchant" : "createMerchant"]({
          name,
          merchantMnemonic,
          website,
        });

        if (response?.status !== 200) {
          throw new Error(response.data.message || "Failed to create MID");
        }
        const { message = `MID created successfully` } = response?.data;
        showToast(message, "success");
        setIsLoading(false);

        setIsMidLoading(true);
        await refreshData();
        setIsMidLoading(false);
      } catch (error) {
        const errMsg = error?.response?.data?.error?.message || error?.data?.message || error?.message || "Some error";
        showToast(errMsg, "error");
        setIsLoading(false);
      } finally {
        onClose();
        formik.resetForm();
        setIsLoading(false);
      }
    },
  });

  const onCloseClick = () => {
    formik.resetForm();
    onClose();
  };

  useEffect(() => {
    formik.resetForm({
      values: {
        orgLevel: formik.values.orgLevel,
        name: "",
        merchantMnemonic: "",
        website: "",
      },
    });
  }, [formik.values.orgLevel]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalCloseButton />
      <ModalContent>
        <ModalHeader>
          <HStack justify="center" textAlign="center" color="#1A4FD6">
            <Text></Text>
          </HStack>
        </ModalHeader>
        <ModalBody textAlign="center">
          <Box maxW={"600px"} p={4} mx="0" ml="0">
            <form onSubmit={formik.onSubmit}>
              {/* Entity Type */}
              <FormControl as="fieldset" mb={4} isInvalid={formik.touched.orgLevel && !!formik.errors.orgLevel}>
                <FormLabel as="legend">Select Entity Type</FormLabel>
                <RadioGroup name="orgLevel" onChange={(value) => formik.setFieldValue("orgLevel", value)} value={formik.values.orgLevel}>
                  <Stack direction="row">
                    <Radio value="Merchant">Merchant</Radio>
                    <Radio value="Sub Merchant">Sub Merchant</Radio>
                  </Stack>
                </RadioGroup>
                <FormErrorMessage>{formik.errors.orgLevel}</FormErrorMessage>
              </FormControl>
              <FormControl mb={4} isInvalid={formik.touched.name && !!formik.errors.name}>
                <FormLabel>Name</FormLabel>
                <Input name="name" value={formik.values.name} onChange={formik.handleChange} placeholder="Enter Org name" />
                <FormErrorMessage>{formik.errors.name}</FormErrorMessage>
              </FormControl>

              {/* Mnumonic of merhcant, for creating a submerchant*/}
              {formik.values.orgLevel === "Sub Merchant" && (
                <FormControl mb={4} isInvalid={formik.touched.merchantMnemonic && !!formik.errors.merchantMnemonic}>
                  <FormLabel>Merchant</FormLabel>
                  <Select
                    name="merchantMnemonic"
                    value={formik.values.merchantMnemonic}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Select merchant"
                  >
                    {merchantOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} ({option.value})
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{formik.errors.merchantMnemonic}</FormErrorMessage>
                </FormControl>
              )}

              <FormControl mb={4} isInvalid={formik.touched.website && !!formik.errors.website}>
                <FormLabel>Website</FormLabel>
                <Input name="website" value={formik.values.website} onChange={formik.handleChange} placeholder="Enter Website" disabled={isLoading} />
                <FormErrorMessage>{formik.errors.website}</FormErrorMessage>
              </FormControl>
            </form>
          </Box>
        </ModalBody>
        <ModalFooter justifyContent="center">
          <HStack>
            {/* Submit Button */}
            <Button type="submit" onClick={formik.handleSubmit} bgColor={"#1A4FD6"} _hover={{ bgColor: "#163FB3" }} mr={3} isLoading={isLoading}>
              Create MID
            </Button>
            <Button border="1px" variant={"outline"} borderColor="#1A4FD6" onClick={onCloseClick} isDisabled={isLoading}>
              Close
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateMIDModal;
